import { useCallback } from 'react';
import { useProductTagging } from '../../context';
import type { TagDataUseToCreateTag } from '@modules/product-tagging/type';
import TaggingModalForm from '@modules/product-tagging/components/tagging-modal-form/TaggingModalForm';
import type { TaggingFormFieldDescriptor } from '@modules/product-tagging/components/tagging-modal-form/TaggingModalForm';

const FIELDS: TaggingFormFieldDescriptor[] = [
    {
        name: 'productName',
        placeholder: 'Product name',
        maxLength: 255,
        autoFocus: true,
    },
    {
        name: 'productURL',
        placeholder: 'https://supplier.com/product-page',
        maxLength: 255,
        isURL: true,
    },
];

interface ProductDetailsFormProps {
    /** The supplier selected in the previous step. */
    supplier: {
        id: number;
        title: string;
        link: string;
        offPlatformStub?: boolean;
        websiteURL?: string;
    };
    onSubmit: (data: TagDataUseToCreateTag) => void;
    /** Called when the user dismisses the modal without submitting. */
    onDismiss?: () => void;
}

/**
 * Collects product name and URL after supplier selection. In-library suppliers use
 * {@see professionalForUnknownProduct}; off-platform stubs still create prospecting tags.
 */
const ProductDetailsForm = ({
    supplier,
    onSubmit,
    onDismiss: onDismissProp,
}: ProductDetailsFormProps) => {
    const { dispatch } = useProductTagging();

    const dismiss = useCallback(() => {
        dispatch({ type: 'ADDING_TAG', payload: undefined });
        onDismissProp?.();
    }, [dispatch, onDismissProp]);

    const buildPayload = useCallback(
        (values: Record<string, string>): TagDataUseToCreateTag => {
            if (supplier.offPlatformStub) {
                return {
                    titleForUnknownProfessional: supplier.title,
                    offPlatformProfessionalID: supplier.id,
                    titleForUnknownProduct: values.productName,
                    urlForUnknownProduct: values.productURL,
                };
            }

            return {
                professionalForUnknownProduct: {
                    id: supplier.id,
                    title: supplier.title,
                    link: supplier.link,
                },
                titleForUnknownProduct: values.productName,
                urlForUnknownProduct: values.productURL,
            };
        },
        [supplier],
    );

    const message = supplier.offPlatformStub ? (
        <>
            Add the product name and a link to the product page on{' '}
            {supplier.title}&apos;s website so we can follow up.
        </>
    ) : (
        <>
            This product isn&apos;t listed on ArchiPro yet. Add its name and a
            link to the product page so we can notify the supplier.
        </>
    );

    return (
        <TaggingModalForm
            title="Add product details"
            message={message}
            fields={FIELDS}
            buildPayload={buildPayload}
            onSubmit={onSubmit}
            onDismiss={dismiss}
            formId="product-details-form"
        />
    );
};

export default ProductDetailsForm;
